import { AspectRatio } from '@lumen5/luminary';
import type { TransitionManager } from '@lumen5/luminary/core/transition/TransitionManager';

export const NONE_TRANSITION_URL = null;
export const NONE_GROUP_ID = 'NONE';
export const RECOMMENDED_TRANSITION_URL = 'RECOMMENDED';
export const RECOMMENDED_GROUP_ID = RECOMMENDED_TRANSITION_URL;

// Not a real transition URL — morph lives on the previous scene as `morphEnabled`, not on a gap's
// `transitionUrl`. The transitions tab just presents it as one more option.
export const MORPH_GROUP_ID = 'MORPH';

export type TransitionUrl = TransitionManager['transitionUrl'];
export type SelectTransitionHandler = (url: TransitionUrl) => void;

export enum Direction {
    NONE = 'None',
    LEFT = 'Left',
    RIGHT = 'Right',
    DOWN = 'Down',
    UP = 'Up',
    CENTER = 'Center',
}

type AspectRatioUrls = Record<AspectRatio, string>;

export type TransitionUrlData = {
    groupId: string;
    directionId: Direction;
    aspectRatio: AspectRatio;
};

export const TRANSITIONS: Record<
    string,
    Partial<Record<Direction, Record<AspectRatio, string>>>
> = {};
export const TRANSITION_URL_DATA: Record<string, TransitionUrlData> = {};
export const TRANSITION_GROUP_IDS: string[] = [];
export const TRANSITION_LABELS: Record<string, string> = {};

type TransitionEntry = {
    label: string;
    directions: Array<[Direction, AspectRatioUrls]>;
};

const addTransition = ({ label, directions }: TransitionEntry) => {
    const transitionId = label.split(' ').join('-');
    if (!TRANSITIONS[transitionId]) {
        TRANSITIONS[transitionId] = {};
        TRANSITION_GROUP_IDS.push(transitionId);
        TRANSITION_LABELS[transitionId] = label;
    }
    for (const [direction, urls] of directions) {
        if (!TRANSITIONS[transitionId][direction]) {
            TRANSITIONS[transitionId][direction] = {} as Record<AspectRatio, string>;
        }
        for (const [aspectRatio, url] of Object.entries(urls) as Array<[AspectRatio, string]>) {
            TRANSITIONS[transitionId][direction][aspectRatio] = url;
            TRANSITION_URL_DATA[url] = {
                groupId: transitionId,
                directionId: direction,
                aspectRatio,
            };
        }
    }
};

const addAspectRatio = (
    landscape: string,
    square: string,
    vertical: string,
    fourfive: string,
): AspectRatioUrls => ({
    [AspectRatio.LANDSCAPE]: landscape,
    [AspectRatio.SQUARE]: square,
    [AspectRatio.VERTICAL]: vertical,
    [AspectRatio.FOURFIVE]: fourfive,
});

const addDirection = (dir: Direction, urls: AspectRatioUrls): [Direction, AspectRatioUrls] => [
    dir,
    urls,
];

export const getTransitionUrlForDirection = (
    groupId: string,
    aspectRatio: AspectRatio,
    direction: Direction | undefined,
): TransitionUrl => {
    if (direction) {
        const url = TRANSITIONS[groupId]?.[direction]?.[aspectRatio];
        if (url) return url;
    }
    return getDefaultTransitionUrl(groupId, aspectRatio);
};

export const getDefaultTransitionUrl = (
    groupId: string,
    aspectRatio: AspectRatio,
): TransitionUrl => {
    const transition = TRANSITIONS[groupId];
    if (!transition) return null;
    let transitionUrl: TransitionUrl = null;
    if (transition[Direction.NONE]) {
        transitionUrl = transition[Direction.NONE][aspectRatio] ?? null;
    } else if (transition[Direction.CENTER]) {
        transitionUrl = transition[Direction.CENTER][aspectRatio] ?? null;
    } else if (transition[Direction.LEFT]) {
        transitionUrl = transition[Direction.LEFT][aspectRatio] ?? null;
    } else if (transition[Direction.RIGHT]) {
        transitionUrl = transition[Direction.RIGHT][aspectRatio] ?? null;
    } else if (transition[Direction.UP]) {
        transitionUrl = transition[Direction.UP][aspectRatio] ?? null;
    } else if (transition[Direction.DOWN]) {
        transitionUrl = transition[Direction.DOWN][aspectRatio] ?? null;
    }
    return transitionUrl;
};

// All available transitions should be added below using the addTransition function.

addTransition({
    label: 'Arrow Wipe',
    directions: [
        addDirection(
            Direction.LEFT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Left/ArrowWipe-Left-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Left/ArrowWipe-Left-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Left/ArrowWipe-Left-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Left/ArrowWipe-Left-P.json',
            ),
        ),
        addDirection(
            Direction.RIGHT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Right/ArrowWipe-Right-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Right/ArrowWipe-Right-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Right/ArrowWipe-Right-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Right/ArrowWipe-Right-P.json',
            ),
        ),
        addDirection(
            Direction.UP,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Up/ArrowWipe-Up-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Up/ArrowWipe-Up-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Up/ArrowWipe-Up-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Up/ArrowWipe-Up-P.json',
            ),
        ),
        addDirection(
            Direction.DOWN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Down/ArrowWipe-Down-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Down/ArrowWipe-Down-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Down/ArrowWipe-Down-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/ArrowWipe-Down/ArrowWipe-Down-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Circle Wipe',
    directions: [
        addDirection(
            Direction.LEFT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Left/CircleWipe-Left-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Left/CircleWipe-Left-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Left/CircleWipe-Left-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Left/CircleWipe-Left-P.json',
            ),
        ),
        addDirection(
            Direction.RIGHT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Right/CircleWipe-Right-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Right/CircleWipe-Right-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Right/CircleWipe-Right-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Right/CircleWipe-Right-P.json',
            ),
        ),
        addDirection(
            Direction.UP,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Up/CircleWipe-Up-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Up/CircleWipe-Up-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Up/CircleWipe-Up-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Up/CircleWipe-Up-P.json',
            ),
        ),
        addDirection(
            Direction.DOWN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Down/CircleWipe-Down-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Down/CircleWipe-Down-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Down/CircleWipe-Down-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/CircleWipe-Down/CircleWipe-Down-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Diagonal Forward Wipe',
    directions: [
        addDirection(
            Direction.LEFT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Left/DiagonalForwardWipe-Left-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Left/DiagonalForwardWipe-Left-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Left/DiagonalForwardWipe-Left-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Left/DiagonalForwardWipe-Left-P.json',
            ),
        ),
        addDirection(
            Direction.RIGHT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Right/DiagonalForwardWipe-Right-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Right/DiagonalForwardWipe-Right-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Right/DiagonalForwardWipe-Right-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Right/DiagonalForwardWipe-Right-P.json',
            ),
        ),
        addDirection(
            Direction.UP,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Up/DiagonalForwardWipe-Up-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Up/DiagonalForwardWipe-Up-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Up/DiagonalForwardWipe-Up-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Up/DiagonalForwardWipe-Up-P.json',
            ),
        ),
        addDirection(
            Direction.DOWN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Down/DiagonalForwardWipe-Down-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Down/DiagonalForwardWipe-Down-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Down/DiagonalForwardWipe-Down-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalForwardWipe-Down/DiagonalForwardWipe-Down-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Diagonal Wipe',
    directions: [
        addDirection(
            Direction.LEFT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Left/DiagonalWipe-Left-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Left/DiagonalWipe-Left-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Left/DiagonalWipe-Left-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Left/DiagonalWipe-Left-P.json',
            ),
        ),
        addDirection(
            Direction.RIGHT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Right/DiagonalWipe-Right-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Right/DiagonalWipe-Right-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Right/DiagonalWipe-Right-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Right/DiagonalWipe-Right-P.json',
            ),
        ),
        addDirection(
            Direction.UP,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Up/DiagonalWipe-Up-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Up/DiagonalWipe-Up-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Up/DiagonalWipe-Up-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Up/DiagonalWipe-Up-P.json',
            ),
        ),
        addDirection(
            Direction.DOWN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Down/DiagonalWipe-Down-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Down/DiagonalWipe-Down-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Down/DiagonalWipe-Down-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/DiagonalWipe-Down/DiagonalWipe-Down-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Fade Normal',
    directions: [
        addDirection(
            Direction.NONE,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Fade%20Normal/FadeNormal-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Fade%20Normal/FadeNormal-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Fade%20Normal/FadeNormal-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Fade%20Normal/FadeNormal-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Fade Short',
    directions: [
        addDirection(
            Direction.NONE,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Fade%20Short/FadeShort-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Fade%20Short/FadeShort-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Fade%20Short/FadeShort-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Fade%20Short/FadeShort-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Fade Long',
    directions: [
        addDirection(
            Direction.NONE,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/FadeLong/FadeLong-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/FadeLong/FadeLong-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/FadeLong/FadeLong-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/FadeLong/FadeLong-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Iris',
    directions: [
        addDirection(
            Direction.NONE,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Iris/Iris-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Iris/iris-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Iris/Iris-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Iris/Iris-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Iris Square',
    directions: [
        addDirection(
            Direction.NONE,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/IrisSquare/IrisSquare-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/IrisSquare/irisSquare-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/IrisSquare/IrisSquare-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/IrisSquare/IrisSquare-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Offset Push',
    directions: [
        addDirection(
            Direction.LEFT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Left/Offset%20Push-Left-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Left/Offset%20Push-Left-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Left/Offset%20Push-Left-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Left/Offset%20Push-Left-P.json',
            ),
        ),
        addDirection(
            Direction.RIGHT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Right/Offset%20Push-Right-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Right/Offset%20Push-Right-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Right/Offset%20Push-Right-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Right/Offset%20Push-Right-P.json',
            ),
        ),
        addDirection(
            Direction.UP,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Up/Offset%20Push-Up-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Up/Offset%20Push-Up-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Up/Offset%20Push-Up-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Up/Offset%20Push-Up-P.json',
            ),
        ),
        addDirection(
            Direction.DOWN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Down/Offset%20Push-Down-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Down/Offset%20Push-Down-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Down/Offset%20Push-Down-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/OffsetPush-Down/Offset%20Push-Down-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Push',
    directions: [
        addDirection(
            Direction.LEFT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Left/Push-Left-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Left/Push-Left-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Left/Push-Left-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Left/Push-Left-P.json',
            ),
        ),
        addDirection(
            Direction.RIGHT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Right/Push-Right-L-V5.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Right/Push-Right-S-V2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Right/Push-Right-V-V2.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Right/Push-Right-P.json',
            ),
        ),
        addDirection(
            Direction.UP,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Up/Push-Up-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Up/Push-Up-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Up/Push-Up-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Up/Push-Up-P.json',
            ),
        ),
        addDirection(
            Direction.DOWN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Down/Push-Down-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Down/Push-Down-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Down/Push-Down-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Push-Down/Push-Down-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Slide',
    directions: [
        addDirection(
            Direction.LEFT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Left/Slide-Left-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Left/Slide-Left-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Left/Slide-Left-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Left/Slide-Left-P.json',
            ),
        ),
        addDirection(
            Direction.RIGHT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Right/Slide-Right-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Right/Slide-Right-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Right/Slide-Right-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Right/Slide-Right-P.json',
            ),
        ),
        addDirection(
            Direction.UP,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Up/Slide-Up-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Up/Slide-Up-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Up/Slide-Up-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Up/Slide-Up-P.json',
            ),
        ),
        addDirection(
            Direction.DOWN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Down/Slide-Down-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Down/Slide-Down-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Down/Slide-Down-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Slide-Down/Slide-Down-P.json',
            ),
        ),
    ],
});

addTransition({
    label: 'Wipe',
    directions: [
        addDirection(
            Direction.LEFT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Left/Wipe-Left-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Left/Wipe-Left-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Left/Wipe-Left-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Left/Wipe-Left-P.json',
            ),
        ),
        addDirection(
            Direction.RIGHT,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Right/Wipe-Right-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Right/Wipe-Right-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Right/Wipe-Right-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Right/Wipe-Right-P.json',
            ),
        ),
        addDirection(
            Direction.UP,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Up/Wipe-Up-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Up/Wipe-Up-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Up/Wipe-Up-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Up/Wipe-Up-P.json',
            ),
        ),
        addDirection(
            Direction.DOWN,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Down/Wipe-Down-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Down/Wipe-Down-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Down/Wipe-Down-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Down/Wipe-Down-P.json',
            ),
        ),
        addDirection(
            Direction.CENTER,
            addAspectRatio(
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center/Wipe-Center-L.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center/Wipe-Center-S.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center/Wipe-Center-V.json',
                'https://storage.googleapis.com/lumen5-prod-lottie/Lumen5-Transitions2025/Wipe-Center/Wipe-Center-P.json',
            ),
        ),
    ],
});
